import { Box, Text, useBoolean, VStack } from '@chakra-ui/react';
import { format, isAfter } from 'date-fns';
import { ja } from 'date-fns/locale';
import Decimal from 'decimal.js';
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Navigate,
  useLocation,
  useOutletContext,
  useParams,
} from 'react-router-dom';

import { AppError } from '../../../../../framework/models/errors';
import { ListUtils } from '../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import {
  BackButton,
  EditButton,
  SaveButton,
} from '../../../../components/StyledButton';
import { pageKeywords, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import { usePostAssignWorkInfo } from '../../../../hooks/queries/assignWorkInfo';
import useToastHook from '../../../../hooks/ToastHook';
import { Formatter } from '../../../../utils/FormatUtils';
import { useMasterContext } from '../../../MasterProvider';
import { useConfirmModal } from '../../../modals/confirm';
import { useSizeContext } from '../../../SizeProvider';
import {
  AssignWorkInfoDaySearchResultForm,
  WorkContentOutletContext,
  WorkContentParams,
} from '../WorkContent';
import { useDailyWork } from './DailyWork';
import Matters from './Matters';

/**
 * 就業報告内容プロパティ
 */
export type AssignWorkInfoProps = {
  /**
   * 表示モード
   */
  mode: 'edit' | 'view';
};

/**
 * 就業報告内容
 * @returns
 */
const AssignWorkInfo: React.FunctionComponent<AssignWorkInfoProps> = (
  props
) => {
  const { actions, state } = useGlobalStateMachine();
  const sizes = useSizeContext();
  const params = useParams<WorkContentParams>();
  const location = useLocation();
  const navigate = useEngeneNavigate();
  const form = useFormContext<AssignWorkInfoDaySearchResultForm>();
  const master = useMasterContext();
  const ATTENDANCE_TYPE_PRESENT = React.useMemo<number>(
    () => state.appConfig.facilitySettings?.ATTENDANCE_TYPE_ID_WORK as number,
    [state.appConfig.facilitySettings]
  );
  const { toast } = useToastHook();
  const [recalculateDailyWorkTrigger, { toggle: recalculateDailyWork }] =
    useBoolean(false);

  const { data, matterIndex, extraFeesConcatenated } =
    useOutletContext<WorkContentOutletContext>();

  const matterSize = React.useMemo(
    () => (TypeUtils.isNil(data?.results) ? 0 : data!.results!.length),
    [data?.results]
  );
  const postAssignWorkInfo = usePostAssignWorkInfo();
  const { ConfirmModal, onOpen } =
    useConfirmModal<AssignWorkInfoDaySearchResultForm>();

  const { DailyWork: DailyWorkArea, isAbsence, toggle } = useDailyWork();

  // 全現場の作業開始時刻のうち、一番古いものを取得
  const workTimeFrom = React.useMemo(() => {
    const effectiveAttendanceTypeIndexes = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.attendanceTypeId` as any
          )
      )
      .map((id, index) => (id === ATTENDANCE_TYPE_PRESENT ? index : null))
      .filter((index) => index !== null);

    const values = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.workTimeFrom` as any
          )
      )
      .filter(
        (value, index) =>
          effectiveAttendanceTypeIndexes.includes(index) &&
          !TypeUtils.isNil(value) &&
          value !== ''
      );

    return values.length > 0
      ? Formatter.dateTime(
          new Date(
            values.reduce((prev: string, cur: string) =>
              (Date.parse(Formatter.toSlash(prev)) || 0) >
              (Date.parse(Formatter.toSlash(cur)) || 0)
                ? cur
                : prev
            )
          )
        )
      : null;
  }, [form, recalculateDailyWorkTrigger]);

  // 全現場の作業終了時刻のうち、一番新しいものを取得
  const workTimeTo = React.useMemo(() => {
    const effectiveAttendanceTypeIndexes = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.attendanceTypeId` as any
          )
      )
      .map((id, index) => (id === ATTENDANCE_TYPE_PRESENT ? index : null))
      .filter((index) => index !== null);

    const values = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.workTimeTo` as any
          )
      )
      .filter(
        (value, index) =>
          effectiveAttendanceTypeIndexes.includes(index) &&
          !TypeUtils.isNil(value) &&
          value !== ''
      );

    return values.length > 0
      ? Formatter.dateTime(
          new Date(
            values.reduce((prev: string, cur: string) =>
              (Date.parse(Formatter.toSlash(prev)) || 0) >
              (Date.parse(Formatter.toSlash(cur)) || 0)
                ? prev
                : cur
            )
          )
        )
      : null;
  }, [form, recalculateDailyWorkTrigger]);

  // 総作業時間(全現場の作業時間の総和)を取得
  const totalWorkTime: number = React.useMemo(() => {
    // 出勤となっている matterIndex
    const effectiveAttendanceTypeIndices = form
      .getValues(
        Array(matterSize)
          .fill(null)
          .map(
            (_, index) =>
              `results.${index}.assignedWorkerWorkInfo.attendanceTypeId` as any
          )
      )
      .map((id, index) => (id === ATTENDANCE_TYPE_PRESENT ? index : null))
      .filter(TypeUtils.isNonNilNumber);

    if (state.userInfo?.roleOptions.isOutsourcer) {
      return form
        .getValues(
          Array(matterSize)
            .fill(null)
            .map(
              (_, index) =>
                `results.${index}.assignedWorkerWorkInfo.workTime` as any
            )
        )
        .filter(
          (value, index) =>
            effectiveAttendanceTypeIndices.includes(index) &&
            !TypeUtils.isNil(value)
        )
        .map((value) => parseInt(value.toString()))
        .filter(TypeUtils.isNonNilNumber)
        .reduce((prev: number, cur: number) => prev + cur, 0);
    } else {
      // SE の場合は勤務時間 - 休憩時間
      if (
        TypeUtils.isNilString(workTimeFrom) ||
        TypeUtils.isNilString(workTimeTo)
      ) {
        return 0;
      }
      const from = Formatter.createDate(workTimeFrom);
      const to = Formatter.createDate(workTimeTo);
      const totalRestTime = form
        .getValues(
          Array(matterSize)
            .fill(null)
            .map(
              (_, index) =>
                `results.${index}.assignedWorkerWorkInfo.restTime` as any
            )
        )
        .filter(
          (value, index) =>
            effectiveAttendanceTypeIndices.includes(index) &&
            !TypeUtils.isNil(value)
        )
        .map((value) => parseInt(value.toString()))
        .filter(TypeUtils.isNonNilNumber)
        .reduce((prev: number, cur: number) => prev + cur, 0);

      return new Decimal(to.valueOf() - from.valueOf())
        .dividedToIntegerBy(60000)
        .minus(totalRestTime)
        .toNumber();
    }
  }, [form, recalculateDailyWorkTrigger]);

  // 残業有無(総作業時間が8時間を越える)
  const isOvertime = React.useMemo(
    () =>
      totalWorkTime > 480 &&
      !TypeUtils.isNilString(workTimeFrom) &&
      !TypeUtils.isNilString(workTimeTo),
    [totalWorkTime, recalculateDailyWorkTrigger]
  );

  // 保存前バリデーション
  const validateForSave = () => {
    const warns: string[] = [];
    const regexp = new RegExp(/^\d*[05]$/);
    Array(matterSize)
      .fill(null)
      .forEach((_, index) => {
        // 作業状況
        const attendanceTypeId = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.attendanceTypeId`
        );
        // 作業開始
        const workTimeFrom = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.workTimeFrom`
        );
        // 作業終了
        const workTimeTo = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.workTimeTo`
        );
        // 休憩時間
        const restTime = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.restTime`
        );
        const workTime = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.workTime`
        );

        if (
          TypeUtils.isNilNumber(attendanceTypeId) &&
          TypeUtils.isNonNilNumber(
            (data?.results || [])[index]?.assignedWorkerWorkInfo
              .attendanceTypeId
          )
        ) {
          warns.push(`現場${index + 1}:作業状況を未選択に変更できません`);
        }

        // 作業状況が出勤の場合、開始時刻 < 終了時刻 であること
        if (
          attendanceTypeId === ATTENDANCE_TYPE_PRESENT &&
          isAfter(
            Formatter.createDate(workTimeFrom!),
            Formatter.createDate(workTimeTo!)
          )
        ) {
          warns.push(`現場${index + 1}：開始時刻 < 終了時刻 で入力して下さい`);
        }

        // 作業状況が出勤の場合、休憩時間は5分単位であること
        if (
          attendanceTypeId === ATTENDANCE_TYPE_PRESENT &&
          !regexp.test('' + restTime)
        ) {
          warns.push(`現場${index + 1}：休憩時刻は5分単位 で入力して下さい`);
        }

        if ((workTime || 0) < 0) {
          warns.push(`現場${index + 1}: 休憩時間が労働時間を超えています`);
        }
      });
    return warns;
  };

  // 保存ボタンの制御
  const [canSave, setCanSave] = useState(false);
  // 保存ボタンの制御に関わる項目で再計算必要となるタイミングで呼び出すメソッド
  const checkCanSave = () => {
    if (
      Array(matterSize)
        .fill(null)
        .map((_, index) =>
          form.getValues(
            `results.${index}.assignedWorkerWorkInfo.attendanceTypeId`
          )
        )
        .every(TypeUtils.isNilNumber)
    ) {
      setCanSave(false);
      return;
    }
    const b = Array(matterSize)
      .fill(null)
      .some((_, index) => {
        // 作業状況
        const attendanceTypeId = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.attendanceTypeId`
        );
        // 休憩時間
        const restTime = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.restTime`
        );
        // 残業承認時刻
        const overtimeApprovedTime = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.overtimeApprovedTime`
        );
        // 残業承認者
        const overtimeApprovedPerson = form.getValues(
          `results.${index}.assignedWorkerWorkInfo.overtimeApprovedPerson`
        );
        // 作業時間
        const workTime =
          form.getValues(`results.${index}.assignedWorkerWorkInfo.workTime`) ||
          0;

        // チェック：入力必須
        // 以下 OR 条件で何かに引っかかったら true -> save できない
        const checkRequiredsNotFullfilled: boolean =
          // TypeUtils.isNilNumber(attendanceTypeId) ||
          (attendanceTypeId === ATTENDANCE_TYPE_PRESENT &&
            form
              .getValues([
                `results.${index}.assignedWorkerWorkInfo.workTimeFrom`,
                `results.${index}.assignedWorkerWorkInfo.workTimeTo`,
                `results.${index}.assignedWorkerWorkInfo.restTime`,
              ])
              .filter((v) => TypeUtils.isNilStrict(v?.toString())).length >
              0) ||
          (TypeUtils.isNilStrict(restTime) ? false : restTime < 0) ||
          (workTime > 480 &&
            (TypeUtils.isNilString(overtimeApprovedTime) ||
              TypeUtils.isNilString(overtimeApprovedPerson)));
        return checkRequiredsNotFullfilled;
      });
    setCanSave(!b);
  };
  React.useEffect(() => {
    // 画面表示時に一度再計算
    checkCanSave();
  });

  // edit で来ても、対象が外注で全ての手配職人が承認済みなら(承認時刻が入っていれば)、編集不可として VIEW モードへ飛ばす。
  // 編集可否は勤怠申請の有無のみで判定されるが、外注の場合は全ての手配職人が了承済の時点で編集不可となるため(手当・ポイントの編集が無い)ため
  if (
    props.mode === 'edit' &&
    (state.userInfo?.role_id.includes(master.role.shift!.id!)
      ? data?.results?.every(
          (result) =>
            !TypeUtils.isNilString(
              result.assignedWorkerSummary.reportApprovedTime
            )
        )
      : data?.results?.every(
          (result) =>
            !TypeUtils.isNilString(
              result.assignedWorkerSummary.reportApprovedTime
            ) &&
            !TypeUtils.isNilString(
              result.assignedWorkerSummary.outsourcerUserCode
            )
        ) || TypeUtils.isNonNilNumber(data?.reference?.dailyWork?.workApplyId))
  ) {
    return (
      <Navigate
        replace
        to={`${pagePaths.WORK_CONTENT}/${params.date}/${params.usercode}/${pageKeywords.mode.VIEW}${location.search}`}
      />
    );
  }

  return (
    <VStack
      alignItems="start"
      as="form"
      fontSize={sizes.fontSize.rawMessage}
      // onSubmit={form.handleSubmit(onOpen)}
      width={sizes.width.styledMessageList}
    >
      <VStack alignItems="start" width="100%">
        <Text margin="0px !important">
          {Formatter.dateOfWeekString(params.date)}
        </Text>
        <Text margin="0px !important">
          {data?.reference?.userSummary?.name}
        </Text>
      </VStack>
      {matterSize === 0 ? null : (
        <>
          <DailyWorkArea
            isOvertime={isOvertime}
            matterSize={matterSize}
            mode={props.mode}
            totalWorkTime={totalWorkTime}
            workTimeFrom={workTimeFrom}
            workTimeTo={workTimeTo}
          />
          <Matters
            isAbsence={isAbsence}
            matterIndex={matterIndex}
            matterSize={matterSize}
            mode={props.mode}
            toggle={toggle}
            onChangedRelatedToSavable={checkCanSave}
            onChangedRelatedToDailyWork={recalculateDailyWork}
          />
          {/* <Box width="100%">
            {props.mode === 'edit' ? (
              <SaveButton
                isDisabled={!canSave}
                type="button"
                onClick={() => {
                  const warns = validateForSave();
                  if (warns.length > 0) {
                    warns.forEach((warn, index) => {
                      setTimeout(() => {
                        toast(warn, 'warning');
                      }, index * 100);
                    });
                  } else {
                    form.handleSubmit(onOpen)();
                  }
                }}
              />
            ) : data?.results?.every(
                (result) =>
                  !TypeUtils.isNilString(
                    result.assignedWorkerSummary.reportApprovedTime
                  )
              ) ? null : (
              <EditButton
                onClick={() =>
                  navigate(
                    `${pagePaths.WORK_CONTENT}/${params.date}/${params.usercode}/${pageKeywords.mode.EDIT}${location.search}`
                  )
                }
              />
            )}
          </Box> */}
          <Box width="100%">
            {props.mode === 'edit' ? (
              <SaveButton
                isDisabled={!canSave}
                type="button"
                onClick={() => {
                  const warns = validateForSave();
                  if (warns.length > 0) {
                    warns.forEach((warn, index) => {
                      setTimeout(() => {
                        toast(warn, 'warning');
                      }, index * 100);
                    });
                  } else {
                    form.handleSubmit(onOpen)();
                  }
                }}
              />
            ) : null}
          </Box>
        </>
      )}
      <BackButton onClick={() => navigate(-1)} />
      <ConfirmModal
        message="保存してよろしいですか?"
        onOk={(value) => {
          const extraFeesString = value?.extraFees?.map((extraFeeId) =>
            parseInt(extraFeeId.toString())
          );
          /**
           * 編集で追加された給与手当 ID
           */
          const addedExtraFeeIds =
            extraFeesString?.filter(
              (id) => !extraFeesConcatenated?.includes(id)
            ) || [];
          const removedExtraFeeIds =
            extraFeesConcatenated?.filter(
              (id) => !extraFeesString?.includes(id)
            ) || [];

          actions.setGuard(true);
          postAssignWorkInfo.mutate(
            {
              assignWorkers: value?.results
                // ?.filter((result) =>
                //   TypeUtils.isNilString(
                //     result.assignedWorkerSummary.reportApprovedTime
                //   )
                // )
                ?.sort(
                  ListUtils.sort((result) =>
                    TypeUtils.isNilString(
                      result.assignedWorkerWorkInfo.workTimeFrom
                    )
                      ? undefined
                      : Date.parse(result.assignedWorkerWorkInfo.workTimeFrom)
                  )
                )
                .filter((result) =>
                  TypeUtils.isNonNilNumber(
                    result.assignedWorkerWorkInfo.attendanceTypeId
                  )
                )
                .map((result, index) => {
                  const isPresent =
                    result.assignedWorkerWorkInfo.attendanceTypeId ===
                    ATTENDANCE_TYPE_PRESENT;
                  const isOvertime =
                    TypeUtils.isNilStrict(
                      result.assignedWorkerWorkInfo.workTimeFrom
                    ) ||
                    TypeUtils.isNilStrict(
                      result.assignedWorkerWorkInfo.workTimeTo
                    )
                      ? false
                      : (Date.parse(
                          result.assignedWorkerWorkInfo.workTimeTo || ''
                        ) -
                          Date.parse(
                            result.assignedWorkerWorkInfo.workTimeFrom || ''
                          )) /
                          (1000 * 60) -
                          parseInt(
                            result.assignedWorkerWorkInfo.restTime?.toString() ||
                              '0'
                          ) >
                        480;

                  return {
                    assignWorkerId: result.assignedWorkerSummary.assignWorkerId,
                    assignWorkerVersion:
                      result.assignedWorkerSummary.assignWorkerVersion,
                    userCodeOutsourceMember: TypeUtils.toStrictValue(
                      result.assignedWorkerSummary.outsourcerUserCode
                    ),
                    taskIds: result.assignedWorkerSummary.taskIds,
                    attendanceTypeId:
                      result.assignedWorkerWorkInfo.attendanceTypeId,

                    // 出勤時以外クリア
                    beLate: isPresent
                      ? !!result.assignedWorkerWorkInfo.beLate
                      : false,

                    // 出勤時以外クリア
                    leaveWork: isPresent
                      ? !!result.assignedWorkerWorkInfo.leaveWork
                      : false,

                    // 出勤時以外クリア
                    timeFrom: isPresent
                      ? Formatter.dateTimeString(
                          result.assignedWorkerWorkInfo.workTimeFrom,
                          true
                        )
                      : undefined,

                    // 出勤時以外クリア
                    timeTo: isPresent
                      ? Formatter.dateTimeString(
                          result.assignedWorkerWorkInfo.workTimeTo,
                          true
                        )
                      : undefined,

                    // 出勤時以外クリア
                    restTime: isPresent
                      ? TypeUtils.toStrictValue(
                          parseInt(
                            result.assignedWorkerWorkInfo.restTime?.toString() ||
                              ''
                          )
                        )
                      : 0,

                    // 出勤時かつ残業発生時以外クリア
                    overtimeApprovedTime:
                      isPresent && isOvertime
                        ? Formatter.dateTimeString(
                            result.assignedWorkerWorkInfo.overtimeApprovedTime,
                            true
                          )
                        : undefined,

                    // 出勤時かつ残業発生時以外クリア
                    overtimeApprovedPerson:
                      isPresent && isOvertime
                        ? TypeUtils.toStrictValue(
                            result.assignedWorkerWorkInfo.overtimeApprovedPerson
                          )
                        : undefined,

                    reportMemo: TypeUtils.toStrictValue(
                      result.assignedWorkerWorkInfo.reportMemo
                    ),
                    updateExtraFee:
                      value.extraFees?.sort() !==
                      value.results
                        ?.flatMap((result) => result.extraFeeIds)
                        .sort(),
                    extraFeeMasterIds: (result.extraFeeIds || [])
                      ?.concat(
                        // 追加されたものは 1　現場目に紐づけておく
                        index === 0 ? addedExtraFeeIds : []
                      )
                      .map((v) => parseInt(v.toString()))
                      .filter((v) => !removedExtraFeeIds.includes(v)),
                  };
                }),
            },
            {
              onSuccess: () => {
                navigate(-1);
                actions.setGuard(false);
              },
              onError: (error) => {
                toast(
                  error instanceof AppError ? error : '就業報告に失敗しました'
                );
                actions.setGuard(false);
              },
            }
          );
        }}
      />
    </VStack>
  );
};

export default AssignWorkInfo;
